import { notifications } from '@mantine/notifications';
import { useMutation, useSuspenseQueries } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getLogger } from 'loglevel';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';
import { decodeSlashes, encodeSlashes } from '@apple/utils/url';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { type ProductKey, productKeySchema } from '@apple/features/product/models/product';
import { categoryQueries } from '@apple/features/product/queries/category';
import { productQueryKeys, productQueryOptions } from '@apple/features/product/queries/management';
import { deleteProduct, saveProduct } from '@apple/features/product-channel/api/management';
import { ProductForm } from '@apple/features/product-channel/components/ProductManagement/ProductForm';
import type { AppleBrandedProduct } from '@apple/features/product-channel/models/management';
import { channelProductQueryOptions } from '@apple/features/product-channel/queries/management';
import { PRODUCTS_ODATA_QUERY_KEY } from '@apple/features/product-channel/queries/odata';

const log = getLogger('manage-product');

export const Route = createFileRoute('/_authed/_admin/manage/products_/$itemId')({
	params: {
		parse: ({ itemId }) => ({
			itemId: productKeySchema.parse(decodeSlashes(itemId)),
		}),
		stringify: ({ itemId }) => ({
			itemId: productKeySchema.parse(encodeSlashes(itemId)),
		}),
	},
	beforeLoad: ({ params: { itemId }, ...args }) => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductManagement.Write',
		});
		return {
			productQueryOptions: channelProductQueryOptions.product(itemId),
			keywordsQueryOptions: productQueryOptions.keywords,
			productVisibilityQueryOptions: channelProductQueryOptions.visibility,
			productLinesQueryOptions: categoryQueries.all,
		};
	},
	loader: ({
		context: {
			queryClient,
			productQueryOptions,
			keywordsQueryOptions,
			productVisibilityQueryOptions,
			productLinesQueryOptions,
		},
	}) => {
		void queryClient.prefetchQuery(productQueryOptions);
		void queryClient.prefetchQuery(keywordsQueryOptions);
		void queryClient.prefetchQuery(productVisibilityQueryOptions);
		void queryClient.prefetchQuery(productLinesQueryOptions);
	},
	wrapInSuspense: true,
	component: EditProductRoute,
});

function EditProductRoute() {
	const { t } = useTranslation('product');
	const goBackToList = useBackOrNavigate();
	const {
		productQueryOptions,
		productLinesQueryOptions,
		productVisibilityQueryOptions,
		keywordsQueryOptions,
		queryClient,
	} = Route.useRouteContext();

	const [productQuery, productLinesQuery, productVisibilityQuery, keywordsQuery] =
		useSuspenseQueries({
			queries: [
				productQueryOptions,
				productLinesQueryOptions,
				productVisibilityQueryOptions,
				keywordsQueryOptions,
			],
		});

	async function handleSuccessfulChange(message: string) {
		void queryClient.invalidateQueries({
			queryKey: [PRODUCTS_ODATA_QUERY_KEY],
		});
		notifications.show({
			message: message,
			icon: <Success />,
			color: 'green',
			autoClose: 5000,
		});
		await goBackToList('/manage/products');
	}

	function showErrorNotification(error: Error) {
		if (!(error instanceof ServerValidationError)) {
			log.error(error);
			notifications.show({
				message: t('common:error.generic'),
				icon: <ErrorIcon />,
				color: 'red',
				autoClose: 5000,
			});
		}
	}

	const updateProductMutation = useMutation({
		mutationFn: async (product: AppleBrandedProduct) => await saveProduct(product, 'N/A'),
		onSuccess: async () => {
			void queryClient.invalidateQueries({
				queryKey: productQueryOptions.queryKey,
			});
			await handleSuccessfulChange(t('notifications.updated'));
		},
		onError: showErrorNotification,
	});

	const deleteProductMutation = useMutation({
		mutationFn: async (itemId: ProductKey) => await deleteProduct(itemId, 'N/A'),
		onSuccess: async () => {
			void queryClient.invalidateQueries({
				queryKey: productQueryKeys.unassignedSkus,
				exact: true,
			});
			queryClient.removeQueries({
				queryKey: productQueryOptions.queryKey,
			});
			await handleSuccessfulChange(t('notifications.deleted'));
		},
		onError: showErrorNotification,
	});

	return (
		<DetailsLayout title={t('titles.editProduct', { itemId: productQuery.data?.itemId })}>
			<ProductForm
				isEdit
				product={productQuery.data}
				productLinesQuery={productLinesQuery}
				productVisibilityQuery={productVisibilityQuery}
				keywordsQuery={keywordsQuery}
				saveProductMutation={updateProductMutation}
				deleteProductMutation={deleteProductMutation}
			/>
		</DetailsLayout>
	);
}
